import React from 'react';
import { Link } from 'react-router-dom';
import { BadgeCheck, BookOpen, Clipboard, HandCoins, ShieldCheck, Stethoscope } from 'lucide-react';
import './HealthSafetyPage.css';
import carethree from '../../assets/images/healthcard.jpg';
import check from '../../assets/images/check.svg';

const SAFETY_PROTOCOLS = [
  {
    title: 'Daily Sanitization',
    description:
      'Classrooms, toys, and high-touch surfaces are cleaned and sanitized multiple times daily using child-safe, EPA-approved products.',
    icon: HandCoins,
  },
  {
    title: 'Daily Health Checks',
    description:
      'All children and staff receive temperature checks and wellness screenings upon arrival each morning.',
    icon: Stethoscope,
  },
  {
    title: 'CPR & First Aid',
    description:
      'All teachers and staff are certified in pediatric CPR and first aid, with annual recertification required.',
    icon: Clipboard,
  },
  {
    title: 'Secure Facility',
    description:
      'Our building features controlled access entry, security cameras, and a sign-in/out system for all visitors.',
    icon: ShieldCheck,
  },
  {
    title: 'Background Checks',
    description:
      'All staff undergo comprehensive background checks including fingerprinting and reference verification.',
    icon: BadgeCheck,
  },
  {
    title: 'Emergency Preparedness',
    description:
      'Regular fire and emergency drills, with detailed emergency plans for various scenarios.',
    icon: BookOpen,
  },
];

const COMPLIANCE_ITEMS = [
  'New York Department of Education',
  'Office of Children & Family Services',
  'New York City Department of Buildings',
  'New York City Fire Department',
];

function HealthSafetyPage() {
  return (
    <main className="health-safety-page">
      {/* Hero Section */}
      <section className="health-safety-hero" aria-label="Health and safety first">
        {/* <div
          className="health-safety-hero-bg"
          style={{ backgroundImage: `url(${resorceBg})` }}
          aria-hidden="true"
        /> */}
        {/* <div className="health-safety-hero-overlay" aria-hidden="true" /> */}
        <div className="health-safety-hero-inner">
          <h1 className="health-safety-hero-title">Health & Safety First</h1>
          <p className="health-safety-hero-desc">
            Your child's health and safety is our top priority. Learn about our comprehensive protocols
            and procedures.
          </p>
        </div>
        {/* <div className="health-safety-hero-wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#fffcf9"
              d="M0 80V40c120 20 240 40 360 40s240-20 360-40 240-40 360-40 240 20 360 40v40H0z"
            />
          </svg>
        </div> */}
      </section>

      {/* Our Safety Protocols */}
      <section className="health-safety-protocols" aria-label="Our safety protocols">
        <div className="health-safety-protocols-inner">
          <h2 className="health-safety-protocols-title">Our Safety Protocols</h2>
          <p className="health-safety-protocols-subtitle">
            We maintain rigorous health and safety standards that exceed regulatory requirements.
          </p>
          <div className="health-safety-protocols-grid">
            {SAFETY_PROTOCOLS.map((protocol, idx) => (
              <article key={idx} className="health-safety-protocol-card">
                <span className="health-safety-protocol-icon" aria-hidden="true">
                  <protocol.icon size={24} strokeWidth={2} />
                </span>
                <h3 className="health-safety-protocol-title">{protocol.title}</h3>
                <p className="health-safety-protocol-desc">{protocol.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Licensing & Compliance */}
      <section className="health-safety-compliance" aria-label="Licensing and compliance">
        <div className="health-safety-compliance-inner">
          <div className="health-safety-compliance-image">
            <img
              src={carethree}
              alt="Children in classroom"
              className="health-safety-compliance-img"
            />
          </div>
          <div className="health-safety-compliance-content">
            <h2 className="health-safety-compliance-title">Licensing & Compliance</h2>
            <p className="health-safety-compliance-subtitle">
              Licensed by NYC Department of Health and Mental Hygiene
            </p>
            <ul className="health-safety-compliance-list">
              {COMPLIANCE_ITEMS.map((item, idx) => (
                <li key={idx} className="health-safety-compliance-item">
                  <span className="health-safety-compliance-check" aria-hidden="true">
                    <img src={check} alt="" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta" id="schedule-tour">
        <div className="cta-inner">
          <h2 className="cta-title">Ready to Apply?
          </h2>
          <p className="cta-desc">
          Start your application today or schedule a tour to learn more about our programs.
          </p>
          <div className="cta-actions">
            <Link to={{ pathname: '/admissions', hash: 'schedule-tour' }} className="btn btn-primary cta-primary">
              <span className="btn-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="3"
                    y="4"
                    width="14"
                    height="13"
                    rx="2"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <path d="M3 8H17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M8 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <path d="M12 3V6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <rect x="7" y="10" width="2.2" height="2.2" rx="0.4" fill="currentColor" />
                </svg>
              </span>
              Schedule Tour
            </Link>
            <Link to="/contact" className="btn btn-outline cta-secondary">
              Inquiry Now
            </Link>
          </div>

          <div className="cta-meta" aria-label="Contact and hours">
            <a className="cta-meta-item" href="tel:718-462-0776">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.5 3.5L9.2 7.2C9.4 7.7 9.3 8.3 8.9 8.7L7.6 10C9 12.7 11.3 15 14 16.4L15.3 15.1C15.7 14.7 16.3 14.6 16.8 14.8L20.5 16.5C21.1 16.8 21.3 17.4 21.1 18C20.6 19.5 19.2 20.5 17.6 20.5C10.1 20.5 3.5 13.9 3.5 6.4C3.5 4.8 4.5 3.4 6 2.9C6.6 2.7 7.2 2.9 7.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">718-462-0776</span>
            </a>

            <div className="cta-meta-item">
              <span className="cta-meta-icon" aria-hidden="true">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8V12L14.5 13.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                </svg>
              </span>
              <span className="cta-meta-text">Monday - Friday: 8:00 AM - 7:00 PM</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default HealthSafetyPage;
